/**
 * "Report an issue" — the pure half (bimgo-ai-web#156). The dialog is
 * `components/ReportIssueDialog.tsx`; everything here takes its collaborators
 * (the bridge, `fetch`, the API base URL, the Authorization header) as values so
 * it can be tested with stubs.
 *
 * The ticket goes to api-v2 `POST /tickets`, which files it as a GitHub issue
 * labelled `bimgo-desktop` — the same endpoint the Revit plugin reports through
 * with `bimgo-plugin`. The user attaches at most one screenshot; the logs are the
 * desktop's to attach (`diagnostics.ticketLogs`, bridge v9: today's and the two
 * previous days', from the main process), never a file the user picks.
 *
 * **Logs never block a report.** Against a desktop older than v9 there is no
 * `ticketLogs`, and a failing or empty one is the same as none: the ticket is
 * filed without logs. A report is most needed exactly when something is broken.
 */
import type { TicketLogs } from "../bimgo";

// Mirrors api-v2's limits (app/services/ticket.py) so the user hears before uploading.
export const TICKET_TITLE_MAX_LENGTH = 200;
export const TICKET_DESCRIPTION_MAX_LENGTH = 10_000;
export const TICKET_SCREENSHOT_MAX_MB = 3;
export const TICKET_SCREENSHOT_EXTENSIONS = [".png", ".jpg", ".jpeg", ".gif", ".webp"];

/** The label api-v2 files the issue under. */
export const TICKET_SOURCE = "bimgo-desktop";

export type ScreenshotError = "unsupported_type" | "exceeds_size" | "empty";

export function validateScreenshot(file: { name: string; size: number }): ScreenshotError | null {
  const name = file.name.toLowerCase();
  if (!TICKET_SCREENSHOT_EXTENSIONS.some((ext) => name.endsWith(ext))) return "unsupported_type";
  if (file.size > TICKET_SCREENSHOT_MAX_MB * 1024 * 1024) return "exceeds_size";
  if (file.size === 0) return "empty";
  return null;
}

/** The minimal window shape detection reads — `window` itself, or a stub. */
export interface TicketLogsHost {
  bimgo?: { diagnostics?: { ticketLogs?: unknown } | undefined } | undefined;
}

/** The bridge's `ticketLogs`, or null when this desktop (or a plain browser) has none. */
export function detectTicketLogs(host: TicketLogsHost | undefined): (() => Promise<TicketLogs | null>) | null {
  const fn = host?.bimgo?.diagnostics?.ticketLogs;
  return typeof fn === "function" ? (fn as () => Promise<TicketLogs | null>) : null;
}

/** Ask the desktop for the ticket's logs; any failure is "no logs", never a thrown report. */
export async function collectTicketLogs(
  ticketLogs: (() => Promise<TicketLogs | null>) | null,
): Promise<TicketLogs | null> {
  if (!ticketLogs) return null;
  try {
    const logs = await ticketLogs();
    return logs && logs.data && logs.data.byteLength > 0 ? logs : null;
  } catch {
    return null;
  }
}

export interface TicketInput {
  title: string;
  description: string;
  screenshot: File | null;
  logs: TicketLogs | null;
  organizationId: string | null;
  /** Where the user was — origin + path only (auth landings carry tokens in the query / hash). */
  pageUrl: string;
  /** One "Client app" row per entry on the issue, e.g. `{ desktop_version: "2.5.3" }`. */
  clientInfo: Record<string, string>;
}

export function buildTicketForm(input: TicketInput): FormData {
  const form = new FormData();
  form.append("title", input.title.trim());
  form.append("description", input.description.trim());
  form.append("source", TICKET_SOURCE);
  form.append("page_url", input.pageUrl);
  if (input.organizationId) form.append("organization_id", input.organizationId);
  if (Object.keys(input.clientInfo).length > 0) {
    form.append("client_info", JSON.stringify(input.clientInfo));
  }
  if (input.screenshot) form.append("files", input.screenshot, input.screenshot.name);
  if (input.logs) {
    form.append("logs", new Blob([new Uint8Array(input.logs.data)], { type: "application/zip" }), input.logs.fileName);
  }
  return form;
}

export interface SubmittedTicket {
  ticket_id: string;
  issue_number: number;
}

export class TicketSubmitError extends Error {
  readonly status: number | null;
  constructor(message: string, status: number | null) {
    super(message);
    this.name = "TicketSubmitError";
    this.status = status;
  }
}

/**
 * api-v2's error bodies: `{ error, message }` from its handler, or FastAPI's own
 * `{ detail }` (a string, or a list for a form it could not parse).
 */
function errorMessage(body: unknown, status: number): string {
  if (body && typeof body === "object") {
    const record = body as Record<string, unknown>;
    if (typeof record["message"] === "string" && record["message"]) return record["message"];
    if (typeof record["detail"] === "string" && record["detail"]) return record["detail"];
  }
  return `Request failed (${status})`;
}

export async function submitTicket(
  input: TicketInput,
  deps: { fetchImpl: typeof fetch; apiBaseUrl: string; authorization: string },
): Promise<SubmittedTicket> {
  let response: Response;
  try {
    response = await deps.fetchImpl(`${deps.apiBaseUrl}/tickets`, {
      method: "POST",
      // No Content-Type: the browser writes the multipart boundary itself.
      headers: { Authorization: deps.authorization },
      body: buildTicketForm(input),
    });
  } catch (error) {
    throw new TicketSubmitError(error instanceof Error ? error.message : String(error), null);
  }

  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // A non-JSON body (a proxy's HTML error page) falls through to the status message.
  }
  if (!response.ok) throw new TicketSubmitError(errorMessage(body, response.status), response.status);
  return (body as { data: SubmittedTicket }).data;
}
